// Notifications Hub client & audio/push helpers (WP-04).
// Subscribes to / polls tenant notifications, manages read status,
// plays new message chimes, and handles FCM web push registration.
import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { stripUndefined } from './callable';
import { useAuth } from './useAuth';
import { useTenant } from './tenant';

export type NotificationType =
  | 'followup_due'
  | 'assignment'
  | 'broadcast_completed'
  | 'broadcast_failed'
  | 'template_approved'
  | 'template_rejected'
  | string;

export interface NotificationDoc {
  id: string;
  tenantId: string;
  type: NotificationType;
  title: string;
  body: string;
  link?: string;
  data?: Record<string, any>;
  conversationId?: string;
  broadcastId?: string;
  templateDocId?: string;
  recipientUid?: string;
  createdAt: number;
  readAt?: number | null;
}

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => (await fn(stripUndefined(data))).data;
}

export const listNotifications = call<
  { limit?: number; unreadOnly?: boolean },
  { ok: boolean; items: NotificationDoc[]; unreadCount: number }
>('notificationList');

export const markNotificationRead = call<{ id: string }, { ok: boolean }>('notificationMarkRead');

export const markAllNotificationsRead = call<Record<string, never>, { ok: boolean; count: number }>(
  'notificationMarkAllRead',
);

export const registerPushToken = call<
  { token: string; userAgent?: string; platform?: string },
  { ok: boolean }
>('notificationRegisterToken');

// ── Web Audio Chime / Sound Fallback ──────────────────────────────────────────
let audioCtx: AudioContext | null = null;

export function playNewMessageChime(): void {
  try {
    const audio = new Audio('/sounds/new-message.mp3');
    audio.volume = 0.6;
    audio.play().catch(() => {
      // Browser autoplay policy might block Audio elements; fall back to Web Audio oscillator
      playWebAudioChime();
    });
  } catch {
    playWebAudioChime();
  }
}

function playWebAudioChime(): void {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    if (!audioCtx) {
      audioCtx = new AudioContextClass();
    }
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume();
    }
    const now = audioCtx.currentTime;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, now); // D5
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.12); // A5

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + 0.35);
  } catch {
    // Audio unallowed or unsupported
  }
}

// ── In-App Browser Notification ───────────────────────────────────────────────
export function showBrowserNotification(title: string, options?: NotificationOptions): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    try {
      new Notification(title, {
        icon: '/favicon-192.png',
        badge: '/favicon-32.png',
        ...options,
      });
    } catch {
      // Mobile chrome requires ServiceWorkerRegistration.showNotification
      if (navigator.serviceWorker) {
        void navigator.serviceWorker.ready.then((reg) => {
          reg.showNotification(title, {
            icon: '/favicon-192.png',
            ...options,
          });
        });
      }
    }
  }
}

// ── Notifications Hook ────────────────────────────────────────────────────────
export function useNotifications() {
  const { tenantId } = useTenant();
  const { user } = useAuth();
  const [items, setItems] = useState<NotificationDoc[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    if (!tenantId || !user) {
      setItems([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    try {
      const res = await listNotifications({ limit: 40 });
      if (res && Array.isArray(res.items)) {
        setItems(res.items);
        setUnreadCount(res.unreadCount ?? res.items.filter((i) => !i.readAt).length);
      }
    } catch {
      // Ignore network errors on background poll
    } finally {
      setLoading(false);
    }
  }, [tenantId, user]);

  const markRead = useCallback(async (id: string) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, readAt: it.readAt ?? Date.now() } : it)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await markNotificationRead({ id });
    } catch {
      // Revert if failed
      void refresh();
    }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    const now = Date.now();
    setItems((prev) => prev.map((it) => ({ ...it, readAt: it.readAt ?? now })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead({});
    } catch {
      void refresh();
    }
  }, [refresh]);

  useEffect(() => {
    void refresh();
    // Poll every 30 seconds for new notifications
    const timer = setInterval(() => {
      void refresh();
    }, 30000);
    return () => clearInterval(timer);
  }, [refresh]);

  return { items, unreadCount, loading, refresh, markRead, markAllRead };
}
